// apiClient.js
// Single owner of the two things every request must carry: the bearer token and the tenant
// (`Subdomain`) header.
//
// Why this file exists (migration plan R-5): both values used to be written directly onto
// `axios.defaults` inside init.js, and the handful of `fetch()` call sites re-implemented the
// header by hand. A forgotten header therefore fell back to the build's dev default, which is
// how a user of one campus ends up reading another campus's data. Everything now resolves
// through this module, and the request interceptor re-reads the values on every call so a
// token set after login is picked up without touching `axios.defaults` again.

import axios from 'axios';

export const SUBDOMAIN_STORAGE_KEY = 'lms-subdomain';
export const TOKEN_STORAGE_KEY = 'lms-jwt';
export const SUBDOMAIN_HEADER = 'Subdomain';

// Only used when the app is opened from localhost/an IP and REACT_APP_DEV_SUBDOMAIN is unset.
// 'makati' is a TVET campus, so a forgotten override shows TVET data instead of CampusCloud's.
const DEV_SUBDOMAIN_FALLBACK = 'makati';

const normalize = (value) => String(value === undefined || value === null ? '' : value).trim().toLowerCase();

const isLocalOrIpHost = (hostname) => {
   const host = String(hostname || '').toLowerCase();

   if (host === '' || host === 'localhost' || host === '127.0.0.1' || host === '::1' || host === '[::1]') {
      return true;
   }

   // 10.0.0.5 / 192.168.1.20: a LAN preview has no tenant subdomain either.
   return /^\d{1,3}(\.\d{1,3}){3}$/.test(host);
};

/**
 * First label of the hostname, or '' when the app was not opened through a tenant host
 * (localhost, an IP, or a bare hostname).
 */
export const resolveSubdomainFromHost = (hostname) => {
   const host = String(hostname === undefined ? window.location.hostname : hostname)
      .split(':')[0]
      .toLowerCase();

   if (isLocalOrIpHost(host) || !host.includes('.')) {
      return '';
   }

   return normalize(host.split('.')[0]);
};

/**
 * The tenant this browser talks to: the host when there is one, otherwise the dev override,
 * otherwise the TVET default. Written to localStorage so every existing
 * `localStorage.getItem('lms-subdomain')` call site keeps working unchanged.
 */
export const getSubdomain = () => {
   const fromHost = resolveSubdomainFromHost();

   if (fromHost) {
      return fromHost;
   }

   const override = normalize(process.env.REACT_APP_DEV_SUBDOMAIN);

   if (override) {
      return override;
   }

   return DEV_SUBDOMAIN_FALLBACK;
};

/**
 * The tenant used inside S3 object URLs (`{S3}/{tenant}/{path}`) — the folder an upload writes to
 * and the folder a later read has to look in, so the two must never disagree.
 *
 * Prefers the tenant recorded at login (`lms-subdomain`), because that is what the legacy pages
 * this replaces were disabled from reading, and falls back to exactly the resolution the request
 * header uses. Migration plan X-2: the assessment editors hardcoded `"ccph"` here, so a TVET
 * campus asked for `…/ccph/study_plan_1.pdf` and got a 404 (or another campus's file).
 */
export const getAssetSubdomain = () => {
   try {
      const stored = normalize(localStorage.getItem(SUBDOMAIN_STORAGE_KEY));

      if (stored) {
         return stored;
      }
   } catch (error) {
      // Storage disabled (private browsing): fall through to host/env resolution.
   }

   return getSubdomain();
};

export const getToken = () => {
   try {
      return localStorage.getItem(TOKEN_STORAGE_KEY) || '';
   } catch (error) {
      return '';
   }
};

/**
 * Records the tenant for the current browser session. Called by init.js at start-up and by
 * the login flow once the API has confirmed which tenant the credentials belong to.
 */
export const setSubdomain = (subdomain) => {
   const tenant = normalize(subdomain) || getSubdomain();

   try {
      localStorage.setItem(SUBDOMAIN_STORAGE_KEY, tenant);
   } catch (error) {
      // Private browsing with storage disabled: requests still carry the header from memory.
   }

   return tenant;
};

/**
 * Headers for call sites that cannot use axios (fetch, new Image(), sendBeacon, ...).
 */
export const tenantHeaders = (token) => {
   const headers = { [SUBDOMAIN_HEADER]: getSubdomain() };
   const jwt = token === undefined ? getToken() : token;

   if (jwt) {
      headers.Authorization = `Bearer ${jwt}`;
   }

   return headers;
};

/**
 * axios instance for new code. Legacy modules that import the global axios default export
 * keep working: `configureApiClient()` sets the same defaults on it.
 */
const apiClient = axios.create();

apiClient.interceptors.request.use((config) => {
   const headers = { ...(config.headers || {}), ...tenantHeaders() };

   config.headers = headers;

   return config;
});

/**
 * Applies the tenant identity to the global axios defaults, which is what the existing
 * services (LoginService, LessonService, ...) read on every call.
 *
 * Returns the configured instance so a caller can chain or inject it in tests.
 */
export const configureApiClient = (subdomain) => {
   const tenant = setSubdomain(subdomain);
   const jwt = getToken();

   axios.defaults.headers.common = jwt
      ? { Authorization: `Bearer ${jwt}`, [SUBDOMAIN_HEADER]: tenant }
      : { [SUBDOMAIN_HEADER]: tenant };

   return apiClient;
};

/**
 * Drops the bearer token from the global defaults, keeping the tenant in effect, which is what
 * logout needs: the tenant is the browser's, not the user's, and the next login re-installs both
 * through `configureApiClient()`.
 *
 * Exists so no screen has to write `axios.defaults` itself (R-5). The sidebar hook used to
 * `delete axios.defaults.headers.common.Authorization` after removing the token from storage —
 * harmless for `apiClient`, whose interceptor re-reads storage on every request, but a second
 * writer of a value that is supposed to have one.
 */
export const clearApiClient = () => {
   const current = axios.defaults.headers.common || {};

   axios.defaults.headers.common = {
      [SUBDOMAIN_HEADER]: current[SUBDOMAIN_HEADER] || getSubdomain(),
   };

   return apiClient;
};

export default apiClient;
